import { makeInMemoryAdapter } from "@livestore/adapter-web";
import { LiveStoreProvider, useStore } from "@livestore/react";
import type React from "react";
import { createContext, useContext, useMemo } from "react";
import { unstable_batchedUpdates as batchUpdates } from "react-dom";

import { AutocompleteInput } from "./components/AutocompleteInput.tsx";
import { events, schema } from "./livestore/schema.ts";
import { createAutocompleteScope } from "./scope.ts";
import type { AutocompleteVM } from "./types.ts";

const AutocompleteVMContext = createContext<AutocompleteVM | null>(null);

export const useAutocompleteVM = () => {
  const vm = useContext(AutocompleteVMContext);
  if (!vm) throw new Error("useAutocompleteVM must be used within AutocompleteVMProvider");
  return vm;
};

const AutocompleteVMProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { store } = useStore();
  const vm = useMemo(() => {
    // Initialize UI state first
    store.commit(events.uiStateSet({ inputText: "", selectedIndex: 0 }));

    // Initialize with some sample files
    store.commit(
      events.fileAdded({ path: "src/index.html" }),
      events.fileAdded({ path: "src/index.ts" }),
      events.fileAdded({ path: "src/utils/index.ts" }),
      events.fileAdded({ path: "src/components/Button.tsx" }),
      events.fileAdded({ path: "src/components/Input.tsx" }),
      events.fileAdded({ path: "src/lib/helpers.ts" }),
      events.fileAdded({ path: "tests/index.test.ts" }),
      events.fileAdded({ path: "tests/integration.test.ts" }),
      events.fileAdded({ path: "README.md" }),
      events.fileAdded({ path: "package.json" }),
    );
    return createAutocompleteScope(store);
  }, [store]);
  return <AutocompleteVMContext.Provider value={vm}>{children}</AutocompleteVMContext.Provider>;
};

const AppBody: React.FC = () => {
  const vm = useAutocompleteVM();
  return (
    <div data-loc="phosphor-blog/content/_sources/autocomplete-demo/Root.tsx:47:10" data-name="div" className="p-6 max-w-2xl mx-auto">
      <div data-loc="phosphor-blog/content/_sources/autocomplete-demo/Root.tsx:48:12" data-name="div" className="mb-4">
        <h3 data-loc="phosphor-blog/content/_sources/autocomplete-demo/Root.tsx:49:13" data-name="h3" className="text-sm font-medium mb-2 text-text">File Reference Autocomplete</h3>
        <p data-loc="phosphor-blog/content/_sources/autocomplete-demo/Root.tsx:50:12" data-name="p" className="text-xs text-text-secondary mb-4">
          Type <code data-loc="phosphor-blog/content/_sources/autocomplete-demo/Root.tsx:51:22" data-name="code" className="px-1 py-0.5 bg-bg border border-border rounded text-[10px]">@</code> followed by a filename to
          see autocomplete suggestions. Use arrow keys to navigate, Enter to select, or click an option.
        </p>
      </div>
      <AutocompleteInput vm={vm} />
    </div>
  );
};

const adapter = makeInMemoryAdapter();

export const App: React.FC = () => (
  <LiveStoreProvider
    schema={schema}
    adapter={adapter}
    renderLoading={(_) => <div data-loc="phosphor-blog/content/_sources/autocomplete-demo/Root.tsx:66:32" data-name="div" className="p-6 text-text-secondary text-sm">Loading autocomplete demo ({_.stage})...</div>}
    batchUpdates={batchUpdates}
  >
    <AutocompleteVMProvider>
      <AppBody />
    </AutocompleteVMProvider>
  </LiveStoreProvider>
);
